import { Avatar } from 'antd';
import anybodyImg from 'assets/icon-anybody.png';
import Icon from 'assets/Icons';
import Box from 'components/Box';
import Modal from 'components/Modal';
import appointmentApis from 'features/appointment/services/apis';
import appointmentSelectors from 'features/appointment/services/selectors';
import { find, flatten, sumBy } from 'lodash';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { IServiceItemData } from 'services/shop/types/categories';
import { IStaffItemData } from 'services/shop/types/staff';
import styled, { css } from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import ModalChooseStaffRef, { useModalChooseStaffRef } from 'widgets/Staff/ModalChooseStaffRef';
import IcCheckService from './IcCheckService';
import { AptStatusHandler } from '../../Staff';
import moment, { Moment } from 'moment';

export type ChangeServiceModalCallback = (services: IUIServiceItem[]) => void;
export type ChangeServiceModalRef = {
  open: (
    callback?: ChangeServiceModalCallback,
    filteredServiceIds?: string[]
  ) => void;
  openWithActive: (
    callback?: ChangeServiceModalCallback,
    config?: {
      lastItemEndTime: Moment,
      sameTimeBooking?: boolean,
    }
  ) => void;
};

type IUIServiceItem = {
  requestStaff?: boolean;
  staffId: string;
  staffAvatar?: string;
  staffName?: string;
  amount: number;
  serviceId: string;
  serviceName: string;
  duration: number;
  price: number;
  catId: string;
  startTime?: string;
  endTime?: string;
  anybody?: boolean;
  childServices?: IServiceItemData[];
};

const ChangeServiceModal = forwardRef<ChangeServiceModalRef, {}>((props, ref) => {
  const modalChooseStaffRef = useModalChooseStaffRef();
  const [visible, setVisible] = useState(false);
  const callbackRef = useRef<ChangeServiceModalCallback | undefined>(undefined);
  const categoriesBase = appointmentSelectors.getCategories();
  const [selectedData, setSelectedData] = useState<IUIServiceItem[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [filteredServiceIds, setFilteredServiceIds] = useState<string[]>([]);
  const [categorySelected, setCategorySelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [staffs, setStaffs] = useState<IStaffItemData[]>([]);
  const [lastItemEndTime, setLastItemEndTime] = useState<Moment | null>(null);
  const [sameTimeBooking, setSameTimeBooking] = useState(false);

  const allServices = useMemo(() => {
    return flatten(categoriesBase.map(o => o.services.map(s => s)));
  }, [categoriesBase]);

  const servicesWithTimes = useMemo(() => {
    if (!lastItemEndTime) return [];

    // cursor advances only in cascade mode; clone per-service in sameTime mode
    const cursor = lastItemEndTime.clone();

    return selectedData.map(o => {
      const serviceStart = sameTimeBooking
        ? lastItemEndTime.clone()  // all services share the same start time
        : cursor.clone();          // cascade: start from where previous ended

      const result: IUIServiceItem = {
        ...o,
        startTime: serviceStart.format('MM-DD-YYYY HH:mm'),
        endTime: serviceStart.clone().add(o.duration * o.amount, 'minutes').format('MM-DD-YYYY HH:mm'),
      };

      // Advance cursor only in cascade mode
      if (!sameTimeBooking) {
        cursor.add(o.duration * o.amount, 'minutes');
      }

      return result;
    });
  }, [selectedData, lastItemEndTime, sameTimeBooking]);

  const servicesFilterByCategory = useMemo(() => {
    return categoriesBase.find((category) => category.id === categorySelected)?.services?.filter(o => !filteredServiceIds?.includes(o.id));
  }, [categoriesBase, categorySelected, filteredServiceIds]);

  const getListStaff = async (serviceId: string) => {
    setLoading(true);
    const timeData = servicesWithTimes.find(o => o.serviceId?.toString() === serviceId.toString());
    try {
      const res: IResponseDataBody<IStaffItemData[]> =
        await appointmentApis.getListStaffByTime({ serviceIds: [serviceId], endTime: timeData?.endTime, startTime: timeData?.startTime, type: 'POS' });
      if (res.data.data) {
        setStaffs(res.data.data);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);
    } finally {
      setLoading(false);
    }
  };

  const onAdd = (service: IServiceItemData, partialData?: Partial<IUIServiceItem>) => {
    const serviceId = service.id;
    const selectedItem = find(selectedData, (o: IUIServiceItem) => o.serviceId === serviceId);
    if (!selectedItem) {
      setSelectedData((prev) => {
        return [...prev, {
          amount: 1,
          serviceId,
          staffId: '',
          requestStaff: true,
          staffAvatar: '',
          staffName: '',
          catId: service.catId,
          serviceName: service.serviceName,
          duration: service.childServices?.length ? service.childServices.reduce((acc, curr) => acc + curr.duration, 0) : service.duration,
          price: service.priceSell,
          anybody: false,
          childServices: (service.childServices || []),
          ...partialData || {},
        }];
      });
      return;
    }
    setSelectedData((prev) => {
      return prev.map(o => {
        if (o.serviceId === serviceId) {
          return ({
            ...o,
            amount: o.amount + 1,
          });
        }
        return o;
      });
    });
  };

  const onRemove = (serviceId: string) => {
    setSelectedData((prev) => {
      return prev.map(o => {
        if (o.serviceId === serviceId) {
          return ({
            ...o,
            amount: o.amount - 1,
          });
        }
        return o;
      }).filter(o => o.amount > 0);
    });
  };

  const onRequestStaff = (service: IServiceItemData) => (value: 'request' | 'none-request' | 'online') => {
    const selectedItem = find(selectedData, (o: IUIServiceItem) => o.serviceId === service.id);
    if (!selectedItem) {
      onAdd(service, { requestStaff: true });
      return;
    }
    setSelectedData((prev) => {
      return prev.map(o => {
        if (o.serviceId === service?.id) {
          return ({
            ...o,
            requestStaff: value === 'request',
          });
        }
        return o;
      });
    });
  };

  const onChangeStaffForService = (service: IServiceItemData) => () => {
    getListStaff(service.id);
    modalChooseStaffRef.current?.openWithActiveList((staff) => {
      const selectedItem = find(selectedData, (o: IUIServiceItem) => o.serviceId === service.id);
      if (!selectedItem) {
        onAdd(service, {
          staffId: staff.id,
          staffAvatar: staff.urlImage,
          staffName: staff.firstName,
          anybody: staff.anyBody,
        });
        return;
      }
      setSelectedData((prev) => {
        return prev.map(o => {
          if (o.serviceId === service?.id) {
            return ({
              ...o,
              staffId: staff.id,
              staffAvatar: staff.urlImage,
              staffName: staff.firstName,
              anybody: staff.anyBody,
            });
          }
          return o;
        });
      });
    }, selectedData.map(o => o.staffId));
  };

  const handleClose = () => {
    setVisible(false);
  };
  const onSubmit = () => {
    setVisible(false);
    const flatMapsData = selectedData.flatMap(item =>
      Array.from({ length: item.amount }, () => item as IUIServiceItem)
    );

    const result: IUIServiceItem[] = [];

    flatMapsData.map(item => {
      if (item.childServices?.length) {
        item.childServices.map(o => {
          result.push({
            amount: 1,
            staffId: item.staffId,
            requestStaff: item.requestStaff,
            staffAvatar: item.staffAvatar,
            staffName: item.staffName,
            catId: item.catId,
            anybody: item.anybody,

            serviceId: o.id,
            serviceName: o.serviceName,
            duration: o.duration,
            price: o.priceSell,
          });
        });
      } else {
        result.push(item);
      }
    });

    callbackRef.current?.(result);
  };

  useImperativeHandle(ref, () => ({
    open: (callback?: ChangeServiceModalCallback, filteredServiceIds?: string[]) => {
      setCategorySelected(categoriesBase?.[0]?.id || '');
      setSelectedServiceIds([]);
      setSelectedData([]);
      setFilteredServiceIds(filteredServiceIds || []);
      callbackRef.current = callback;
      setVisible(true);
      setLastItemEndTime(null);
    },
    openWithActive: (callback, _configs) => {
      setCategorySelected(categoriesBase?.[0]?.id || '');
      setSelectedServiceIds([]);
      setSelectedData([]);
      setFilteredServiceIds(filteredServiceIds || []);
      callbackRef.current = callback;
      setVisible(true);
      setLastItemEndTime(_configs?.lastItemEndTime || null);
      setSameTimeBooking(_configs?.sameTimeBooking || false);
    },
  }));

  return (
    <Modal
      modalTitle='Add services'
      visible={visible}
      onClose={handleClose}
      onSubmit={onSubmit}
      width={'1600px'}
      noneBodyStyle={true}
      noneFooterStyle={true}
      footerPadding={0.5}
      okTitle='OK'
      v2={true}
    >
      <Container>
        <CateGoryContainer>
          {categoriesBase.map((category) => {
            const selectedServices = sumBy(selectedData.filter(o => o.catId === category.id).map(o => o.amount));
            return (
              <CategoryItem
                key={category.id}
                selected={categorySelected === category.id}
                onClick={() => setCategorySelected(category.id)}
              >
                <div className='text'>{category.categoryName} {selectedServices ? `(${selectedServices})` : ''}</div>
              </CategoryItem>
            );
          })}
        </CateGoryContainer>
        <ServicesContainer>
          <div className="list-services">
            {servicesFilterByCategory?.map((service) => {
              const selected = selectedData.find(o => o.serviceId === service.id);
              const servicesWithTime = servicesWithTimes.find(o => o.serviceId === service.id);
              return (
                <ServiceItem
                  selected={!!selected}
                  onClick={() => onAdd(service)}
                  key={service.id}
                >
                  {!!selected && (
                    <div className='icon'>
                      <IcCheckService />
                    </div>
                  )}
                  <Box style={{ flex: 1 }}>
                    <Box display='flex' alignItems='center'><div className='name'>{service.serviceName} <span className='price'>({formatCurrency(service.priceSell)})</span></div> </Box>
                    {!!servicesWithTime && <div className='start-time'>{servicesWithTime?.startTime ? moment(servicesWithTime?.startTime, 'MM-DD-YYYY HH:mm').format('hh:mm A') : '--'} - {servicesWithTime?.endTime ? moment(servicesWithTime?.endTime, 'MM-DD-YYYY HH:mm').format('hh:mm A') : '--'} </div>}
                  </Box>
                  <CheckBoxRequest>
                    <AptStatusHandler service value={selected?.requestStaff ? 'request' : 'none-request'} onChange={onRequestStaff(service)} />
                  </CheckBoxRequest>
                  <AddonStaffForService onChangeStaff={onChangeStaffForService(service)} data={selected} />
                  <ActionButton display='flex' alignItems='center' gap='2'>
                    <button type='button' onClick={(e: any) => [e.stopPropagation(), onAdd(service)]}><span>+</span></button>
                    <div className='price'>{selected?.amount || 0}</div>
                    <button type='button' onClick={(e: any) => [e.stopPropagation(), onRemove(service.id)]}><span>-</span></button>
                  </ActionButton>
                </ServiceItem>
              );
            })}
          </div>
        </ServicesContainer>
        <ModalChooseStaffRef anybody loading={loading} staffs={staffs} v2 ref={modalChooseStaffRef} noneSearch />
      </Container>
    </Modal>
  );
});
ChangeServiceModal.displayName = 'ChangeServiceModal';

const AddonStaffForService = ({ data, onChangeStaff }: { data?: IUIServiceItem, onChangeStaff?: () => void; }) => {
  return (
    <ButtonChangeStaffStyled onClick={(e: any) => {
      e.stopPropagation();
      onChangeStaff?.();
    }}>
      {!data?.staffId ?
        <img src={anybodyImg} alt="anybodyImg" style={{ width: 48, height: 48, borderRadius: 48, }} />
        :
        <Avatar src={data?.staffAvatar ? data?.staffAvatar : undefined} style={{ backgroundColor: '#f56a00' }} size={48}>{data?.staffName?.[0]}</Avatar>
      }
      <div style={{ zoom: 1.5 }}>
        <Icon type="changeCustomer" fill='#6FABB6' />
      </div>
    </ButtonChangeStaffStyled>
  );
};

const ButtonChangeStaffStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  &:active {
    background: #eee;
  }
`;


export { ChangeServiceModal };

const ActionButton = styled(Box)`
  .price {
    width: 30px;
    text-align: center;
  }
  button {
    width: 40px;
    height: 40px;
    border-radius: 5px;
    border: 1px solid #CCD4DC;
    background: #FFF;
    span {
      font-size: 24px;
    }
    &:active {
      background: #eee;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CateGoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  padding: 1rem;
`;

const CategoryItem = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 25px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  color: #505050;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  text-transform: capitalize;
  background: #fcfcfd;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  cursor: pointer;

  ${({ selected }) =>
    selected &&
    css`
      color: #fff;
      background: #2d6772;
      box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -3px 0 0 rgba(8, 56, 65, 0.5) inset;
    `}
  .text {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
    ${({ selected }) =>
    selected &&
    css`
        color: #fff;
      `}
  }
`;

const ServicesContainer = styled.div`
  height: 700px;
  overflow-y: auto;
  padding: 0 1rem;
  .list-services {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 16px;
    row-gap: 8px;
  }
`;

const ServiceItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  height: 59px;
  padding: 8px 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 5px;
  background: #e5e6eb;
  box-sizing: border-box;
  border: 2px solid transparent;
  box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.25) inset, 0 8px 4px 3px rgba(255, 255, 255, 0.25) inset;
  transition: all 0.3s ease;
  ${({ selected }) =>
    selected &&
    css`
      transition: all 0.3s ease;
      border-color: #d2464f;
    `}

  .name {
    flex: 1;
    overflow: hidden;
    color: #1d2129;
    text-overflow: ellipsis;
    font-size: 18px;
    font-weight: 400;
    line-height: normal;
  }
  .price {
    color: #1d2129;
    font-size: 18px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .start-time {
    color: #6fabb6;
    font-size: 14px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 100px;
    background: #d2464f;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CheckBoxRequest = styled.label`
  zoom: 1.2;
  user-select: none;
  &:active {
    background: #eee;
  }
  .checkbox-container span {
    color: #4384ff;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 30px; /* 166.667% */
    text-transform: capitalize;
  }
`;
